import {
  ChangeDetectionStrategy,
  Component,
  input,
  InputSignal,
  inject,
  Signal,
  WritableSignal,
  linkedSignal,
  computed,
  effect,
  untracked,
} from '@angular/core';
import { ButtonComponent } from '@lyceo/shared/ui';
import { SelectItem } from '@lyceo/shared/util-models';
import {
  LevelYearSettings,
  SettingsFacade,
} from '@lyceo/lyceo-pupil-app/user/data-access';
import {
  Level,
  LevelDisplayValue,
} from '@lyceo/lyceo-pupil-app/shared/util-models';
import {
  FormFieldComponent,
  FormLabelDirective,
  SelectComponent,
} from '@lyceo/shared/ui-forms';
import {
  FieldTree,
  form,
  FormField,
  required,
  submit,
} from '@angular/forms/signals';

interface LevelYearForm {
  level: string;
  year: string;
}

@Component({
  selector: 'lyceo-level-year-selector',
  imports: [
    ButtonComponent,
    FormFieldComponent,
    FormLabelDirective,
    SelectComponent,
    FormField,
  ],
  templateUrl: './level-year-selector.component.html',
  styleUrl: './level-year-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LevelYearSelectorComponent {
  private readonly settingsFacade: SettingsFacade = inject(SettingsFacade);

  public readonly showToSettings: InputSignal<boolean> = input(true);

  protected levels: SelectItem[] = [
    { label: LevelDisplayValue.VWO, value: Level.VWO },
    { label: LevelDisplayValue.HAVO, value: Level.HAVO },
    { label: LevelDisplayValue.VMBO_TL, value: Level.VMBO_TL },
    { label: LevelDisplayValue.VMBO_GL, value: Level.VMBO_GL },
  ];

  protected years: SelectItem[] = [
    { label: '1', value: '1' },
    { label: '2', value: '2' },
    { label: '3', value: '3' },
    { label: '4', value: '4' },
    { label: '5', value: '5' },
    { label: '6', value: '6' },
  ];

  protected readonly levelYearLoading: Signal<boolean> =
    this.settingsFacade.levelYearLoading;

  private readonly formModel: WritableSignal<LevelYearForm> = linkedSignal({
    source: this.settingsFacade.practiceLevelYear,
    computation: (levelYearSettings) => this.mapFormModel(levelYearSettings),
  });

  protected form: FieldTree<LevelYearForm> = form(
    this.formModel,
    (schemaPath) => {
      required(schemaPath.level);
      required(schemaPath.year);
    },
  );

  protected readonly filteredYears: Signal<SelectItem[]> = computed(() => {
    const level: string = this.form.level().value();
    let maxYear: string = '1';
    switch (level) {
      case 'VWO':
        maxYear = '6';
        break;
      case 'HAVO':
        maxYear = '5';
        break;
      default:
        maxYear = '4';
        break;
    }

    return this.years.filter((year) => year.value <= maxYear);
  });

  constructor() {
    this.resetSelectedYear();
  }

  protected submit(event: Event): void {
    event.preventDefault();

    submit(this.form, async () => {
      const value: LevelYearForm = this.form().value();
      this.settingsFacade.levelYearSelectSaveClicked({
        level: value.level as Level,
        year: +value.year,
      });
    });
  }

  private resetSelectedYear(): void {
    effect(() => {
      const selectedYear: string = untracked(this.form.year().value);
      const filteredYears: SelectItem[] = this.filteredYears();
      const yearAvailable: boolean = filteredYears.some(
        (year) => year.value === selectedYear,
      );

      if (!yearAvailable) {
        this.form.year().reset('');
      }
    });
  }

  private mapFormModel(
    levelYearSettings: LevelYearSettings | undefined,
  ): LevelYearForm {
    return {
      level: levelYearSettings?.level ?? '',
      year: levelYearSettings?.year.toString() ?? '',
    };
  }
}
